import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Archive,
  ChevronRight,
  CircleCheck,
  Square,
  CheckSquare,
  MinusSquare,
  FolderOpen,
  Inbox,
  Landmark,
  Layers,
  MapPin,
  MoreHorizontal,
  Move,
  Info,
  Paperclip,
  SatelliteDish,
  User,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button, Chip, Modal, Select, Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { api, ApiError } from '@/lib/api-client';
import {
  hasComentariosSupport,
  hasComentariosUploadSupport,
  hasResponsaveisSupport,
  useAddComentario,
  useComentarios,
  useMoverCard,
  useProcessoDetail,
  useSetResponsaveis,
  useUpdateProcesso,
} from '../api';
import { useUsers } from '@/modules/usuarios/api';
import { useUpdateImovel } from '@/modules/clientes/api';
import { useNavigate } from '@tanstack/react-router';
import { encodeClienteId } from '@/modules/clientes/cliente-id';
import { fetchCurrentUser } from '@/lib/auth';
import type {
  CardFieldMap,
  DetailFieldMap,
  DetailFieldType,
  Dominio,
  KanbanColumn,
  UserRef,
} from '../types';

export interface DetailTabSpec {
  id: string;
  label: string;
  // Quando definido, substitui o conteúdo da aba (não usa fields). Útil
  // pra abas com fetch próprio (checklist, documentos, etc.).
  slot?: (cardId: string) => ReactNode;
  // Renderizado APÓS os fields da aba. Útil pra blocos extras que não
  // são fields (histórico, lista de eventos, etc.).
  appendSlot?: (cardId: string) => ReactNode;
}

interface Props {
  dominio: Dominio;
  pipe?: string;
  cardId: string | null;
  columns: KanbanColumn[];
  cardFieldMap: CardFieldMap;
  detailFieldMap: DetailFieldMap;
  // Quando passado, renderiza fields agrupados em abas em vez do scroll
  // único. Cada field ganha tab via DetailFieldMap.tab. Abas com slot
  // próprio substituem o conteúdo de fields.
  tabs?: DetailTabSpec[];
  onClose: () => void;
}

function FieldHelp({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-text-muted transition-colors hover:border-accent hover:text-accent"
        aria-label="Orientação de preenchimento"
        title="Orientação de preenchimento"
      >
        <Info size={11} strokeWidth={2} />
      </button>
      {open ? (
        <span className="max-w-[26rem] rounded-md border border-border bg-surface px-2 py-1 text-[0.72rem] normal-case tracking-normal text-text-2 shadow-sm">
          {text}
        </span>
      ) : null}
    </span>
  );
}

// Layout legacy slide-over (.so-row / .so-key / .so-val):
// — header com tag (eyebrow uppercase muted) + título + chip da fase atual
// — corpo com label/valor lado a lado, separados por hairline
// — footer com seletor de coluna e botão Mover
export function DetailDrawer({
  dominio,
  pipe,
  cardId,
  columns,
  cardFieldMap,
  detailFieldMap,
  tabs,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(tabs?.[0]?.id ?? '');
  const { data, isLoading, error } = useProcessoDetail(dominio, cardId);
  const mover = useMoverCard(dominio, pipe);
  const updater = useUpdateProcesso(dominio, cardId, pipe);
  const setResponsaveis = useSetResponsaveis(dominio, cardId, pipe);
  const responsaveisEnabled = hasResponsaveisSupport(dominio);
  const comentariosEnabled = hasComentariosSupport(dominio);
  useEffect(() => {
    if (tabs && tabs.length && !tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]!.id);
    }
  }, [tabs, activeTab]);
  // Edição de campos do imóvel vinculado — espelhado do cadastro do cliente.
  const cpfDono = (data as Record<string, unknown> | undefined)?.cliente_cpf;
  const updateImovel = useUpdateImovel(typeof cpfDono === 'string' ? cpfDono : null);
  const qcInner = useQueryClient();
  const detailKey = ['kanban', dominio, 'detail', cardId ?? ''] as const;
  const [targetColuna, setTargetColuna] = useState<string>('');
  const [cedulaExtractFile, setCedulaExtractFile] = useState<File | null>(null);
  const [cedulaEtapa, setCedulaEtapa] = useState<'enviada_cartorio' | 'corrigida' | 'registrada'>('enviada_cartorio');

  const cedulaExtraction = useMutation({
    mutationFn: async () => {
      if (!cardId || !cedulaExtractFile) {
        throw new ApiError('Anexe a cédula registrada.', 400, 'CEDULA_OBRIGATORIA');
      }
      const body = new FormData();
      body.append('cedula', cedulaExtractFile);
      body.append('etapa_cedula', cedulaEtapa);
      return api(`/api/v2/processos-credito/${cardId}/cedula/extrair`, {
        method: 'POST',
        body,
        schema: z.object({
          ok: z.boolean(),
          documento_id: z.coerce.number(),
          campos_card_atualizados: z.array(z.string()),
          operacao_endividamento: z.object({
            status: z.string(),
            id: z.union([z.coerce.number(), z.null()]),
            campos_pendentes: z.array(z.string()),
          }),
        }).passthrough(),
      });
    },
    onSuccess: () => {
      void qcInner.invalidateQueries({ queryKey: ['kanban', 'credito', pipe ?? 'default'] });
      void qcInner.invalidateQueries({ queryKey: detailKey });
      void qcInner.invalidateQueries({ queryKey: ['operacoes-credito'] });
      setCedulaExtractFile(null);
    },
  });

  useEffect(() => {
    if (data && typeof data === 'object') {
      const current = (data as Record<string, unknown>).kanban_coluna;
      if (current != null) setTargetColuna(String(current));
    }
  }, [data]);

  useEffect(() => {
    setCedulaExtractFile(null);
    setCedulaEtapa('enviada_cartorio');
    cedulaExtraction.reset();
  }, [cardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = cardId !== null;
  const card = (data ?? {}) as Record<string, unknown>;
  const summary = data ? cardFieldMap(card as never) : null;
  const fields = data ? detailFieldMap(card as never) : [];
  const hasComentariosTab = !!tabs?.some((tab) => tab.id === 'comentarios');
  const hasValue = (v: unknown) =>
    v !== null && v !== undefined && v !== '' && v !== '—';
  const modalTitle =
    summary && cardId ? `${summary.title} #${cardId}` : (summary?.title ?? 'Carregando…');

  // Renderiza um grupo de fields agrupado por section, com colapsáveis pra
  // fases passadas. Compartilhado entre modo legado (scroll) e modo tabs.
  function renderFieldGroups(visible: typeof fields): ReactNode {
    if (visible.length === 0) {
      return <p className="text-xs text-text-muted">Sem campos adicionais.</p>;
    }
    const groups = new Map<string, typeof visible>();
    for (const f of visible) {
      const key = f.section ?? '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(f);
    }
    const phaseRe = /^(\d+)\.\s/;
    const entries = Array.from(groups.entries());
    const phaseEntries: typeof entries = [];
    const nonPhaseEntries: typeof entries = [];
    for (const e of entries) {
      if (phaseRe.test(e[0])) phaseEntries.push(e);
      else nonPhaseEntries.push(e);
    }
    const currentPhase = phaseEntries.length > 0
      ? Math.max(...phaseEntries.map(([k]) => Number(phaseRe.exec(k)![1])))
      : null;
    // Apenas seções de "fechamento" (observações, anotações finais) vão pro
    // trailing — depois das fases. As outras (Cliente, Imóvel, Gestão...)
    // ficam todas no leading (antes da fase atual).
    const TRAILING = new Set(['Observações gerais', 'Observações']);
    const leading = nonPhaseEntries.filter(([k]) => !TRAILING.has(k));
    const trailing = nonPhaseEntries.filter(([k]) => TRAILING.has(k));
    const currentEntry = phaseEntries.find(
      ([k]) => Number(phaseRe.exec(k)![1]) === currentPhase,
    );
    const pastEntries = phaseEntries
      .filter(([k]) => Number(phaseRe.exec(k)![1]) !== currentPhase)
      .sort(
        ([a], [b]) =>
          Number(phaseRe.exec(b)![1]) - Number(phaseRe.exec(a)![1]),
      );
    // Render helper que produz uma <section> ou <CollapsibleSection> a partir
    // de uma entry. Reutilizado nas 4 partes (leading, current, past, trailing).
    const renderEntry = (
      [sectionKey, items]: (typeof entries)[number],
      opts?: { collapsible?: boolean; defaultOpen?: boolean },
    ) => {
      const m = phaseRe.exec(sectionKey);
      const isPhase = !!m;
      const phaseN = m ? Number(m[1]) : null;
      const isPastPhase =
        isPhase && currentPhase != null && phaseN! < currentPhase;
      const Icon = sectionIcon(sectionKey);
      const fieldsContent = (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {items.map((f, i) => {
            const empty = !hasValue(f.value);
            const editable = !!f.field;
            const isResponsaveis = f.type === 'responsaveis';
            const isTriagemAceite = f.type === 'triagem_aceite';
            const fullWidth = f.type === 'textarea'
              || f.type === 'multiselect'
              || f.type === 'emitir_proposta_fundiaria'
              || isResponsaveis
              || isTriagemAceite;
            return (
              <div
                key={i}
                className={`flex flex-col gap-0.5${fullWidth ? ' col-span-2' : ''}`}
              >
                <dt className="flex items-center gap-2 text-[0.7rem] uppercase tracking-wide text-text-muted">
                  <span>{f.label}</span>
                  {f.helpText ? <FieldHelp text={f.helpText} /> : null}
                </dt>
                <dd className="break-words" style={{ lineHeight: 1.5 }}>
                  {isResponsaveis ? (
                    <ResponsaveisEditor
                      current={f.responsaveis ?? []}
                      editable={responsaveisEnabled}
                      saving={setResponsaveis.isPending}
                      onSave={(ids, motivo) => setResponsaveis.mutate({ userIds: ids, motivo })}
                    />
                  ) : isTriagemAceite ? (
                    <TriagemAceiteField
                      cardId={cardId ?? ''}
                      {...(pipe ? { pipe } : {})}
                      assignedId={Number(card.responsavel_id || 0)}
                      nome={f.triagemAssistenteNome ?? null}
                      status={f.triagemAssistenteStatus ?? 'pendente'}
                    />
                  ) : f.type === 'emitir_orcamento' ? (
                    <EmitirOrcamentoField
                      clienteCpf={typeof card.cliente_cpf === 'string' ? card.cliente_cpf : null}
                      onOpen={() => {
                        const cpf = typeof card.cliente_cpf === 'string' ? card.cliente_cpf : '';
                        if (!cpf) return;
                        void navigate({
                          to: '/clientes/$id/relatorio/orcamento',
                          params: { id: encodeClienteId(cpf) },
                        });
                      }}
                    />
                  ) : f.type === 'emitir_formulario_prioridade' ? (
                    <EmitirFormularioPrioridadeField
                      cardId={cardId ?? ''}
                      prioridadeAtiva={String(card.pedido_analise_prioritaria || '').trim() === 'sim'}
                    />
                  ) : f.type === 'emitir_formulario_pra' ? (
                    <EmitirFormularioPraField
                      cardId={cardId ?? ''}
                    />
                  ) : f.type === 'emitir_formulario_alteracao_email_sicar' ? (
                    <EmitirFormularioAlteracaoEmailSicarField
                      cardId={cardId ?? ''}
                    />
                  ) : f.type === 'emitir_formulario_cancelamento_car' ? (
                    <EmitirFormularioCancelamentoCarField
                      cardId={cardId ?? ''}
                    />
                  ) : f.type === 'emitir_prada_simplificado' ? (
                    <EmitirPradaSimplificadoField
                      cardId={cardId ?? ''}
                    />
                  ) : f.type === 'emitir_proposta_fundiaria' ? (
                    <EmitirPropostaFundiariaField card={card} />
                  ) : editable ? (
                    <EditableField
                      field={f.field!}
                      type={f.type ?? 'text'}
                      rawValue={
                        f.rawValue !== undefined
                          ? f.rawValue
                          : empty
                          ? null
                          : typeof f.value === 'string' ||
                              typeof f.value === 'number'
                            ? f.value
                            : null
                      }
                      displayValue={empty ? '—' : f.value}
                      {...(f.options ? { options: f.options } : {})}
                      onSave={(payload) => {
                        if (f.entity === 'imovel') {
                          const cd = (data ?? {}) as Record<string, unknown>;
                          const imovelId = cd.imovel_id;
                          if (imovelId == null) return;
                          updateImovel.mutate(
                            { id: String(imovelId), patch: payload },
                            {
                              onSuccess: () => {
                                qcInner.setQueryData(
                                  detailKey,
                                  (old: unknown) => {
                                    if (!old || typeof old !== 'object') return old;
                                    const next: Record<string, unknown> = {
                                      ...(old as Record<string, unknown>),
                                    };
                                    for (const [k, v] of Object.entries(payload)) {
                                      if (k === 'denominacao')
                                        next.imovel_denominacao = v;
                                      else if (k === 'matricula')
                                        next.imovel_matricula = v;
                                    }
                                    return next;
                                  },
                                );
                                void qcInner.invalidateQueries({ queryKey: detailKey });
                              },
                            },
                          );
                          return;
                        }
                        updater.mutate(payload);
                      }}
                      saving={
                        f.entity === 'imovel'
                          ? updateImovel.isPending
                          : updater.isPending
                      }
                    />
                  ) : f.chipTone && !empty ? (
                    <Chip tone={f.chipTone} size="sm">
                      {f.value}
                    </Chip>
                  ) : (
                    <span
                      className={`${empty ? 'text-text-muted' : 'text-text'} whitespace-pre-line`}
                    >
                      {empty ? '—' : f.value}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      );
      if (isPastPhase || opts?.collapsible) {
        return (
          <CollapsibleSection
            key={sectionKey}
            title={sectionKey}
            {...(Icon ? { icon: Icon } : {})}
            {...(typeof opts?.defaultOpen === 'boolean' ? { defaultOpen: opts.defaultOpen } : {})}
          >
            {fieldsContent}
          </CollapsibleSection>
        );
      }
      return (
        <section
          key={sectionKey || '__default__'}
          className="border border-border rounded-lg p-4 bg-surface-2 mb-3"
        >
          {sectionKey && (
            <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted mb-3 flex items-center gap-2">
              {Icon && (
                <span
                  className="inline-flex items-center justify-center rounded-md shrink-0"
                  style={{
                    width: 24,
                    height: 24,
                    background: 'rgba(217,119,87,0.10)',
                    color: '#d97757',
                  }}
                >
                  <Icon size={14} strokeWidth={1.75} />
                </span>
              )}
              <span>{sectionKey}</span>
            </h3>
          )}
          {fieldsContent}
        </section>
      );
    };

    // Layout estilo CRM: leading (Gestão) → "Fase atual" + currentEntry →
    // "Fases anteriores" + pastEntries (colapsáveis) → trailing (Observações).
    // Headers só aparecem quando há fases — preserva compat pra dominios sem fases.
    return (
      <>
        {leading.map((e) => renderEntry(e))}
        {currentEntry && (
          <>
            <SectionDivider label="Fase atual" accent />
            {renderEntry(currentEntry, { collapsible: true, defaultOpen: true })}
          </>
        )}
        {pastEntries.length > 0 && (
          <>
            <SectionDivider label="Fases anteriores" />
            {pastEntries.map((e) => renderEntry(e))}
          </>
        )}
        {trailing.map((e) => renderEntry(e, { collapsible: true, defaultOpen: false }))}
      </>
    );
  }
  const colunaAtualId =
    data && typeof (data as Record<string, unknown>).kanban_coluna !== 'undefined'
      ? String((data as Record<string, unknown>).kanban_coluna)
      : null;
  const colunaAtual = colunaAtualId
    ? columns.find((c) => String(c.id) === colunaAtualId)
    : null;

  function onMove() {
    if (!cardId || !targetColuna) return;
    const extra: Record<string, unknown> = {};
    // Regra do FNO: pra entrar em "Perdido" o motivo é obrigatório.
    // Pergunta antes de bater no backend pra evitar 400.
    const targetCol = columns.find((c) => String(c.id) === String(targetColuna));
    if (dominio === 'credito' && targetCol?.nome === 'Perdido') {
      const cardData = (data ?? {}) as Record<string, unknown>;
      const motivoAtual = (cardData.motivo_perda_cancelamento as string | null | undefined) ?? '';
      const motivo = window.prompt(
        'Informe o motivo da perda/cancelamento (obrigatório para mover para "Perdido"):',
        motivoAtual,
      );
      if (motivo == null) return; // usuário cancelou
      const trimmed = motivo.trim();
      if (!trimmed) {
        window.alert('O motivo é obrigatório para mover para "Perdido".');
        return;
      }
      extra.motivo_perda_cancelamento = trimmed;
    }
    mover.mutate(
      { id: cardId, coluna: targetColuna, extra },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={modalTitle}
      {...(summary?.subtitle ? { description: summary.subtitle } : {})}
      size="2xl"
      footer={
        cardId ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
            <div className="flex items-center gap-2">
              <Select
                value={targetColuna}
                onChange={(e) => setTargetColuna(e.target.value)}
                className="w-52"
              >
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                onClick={onMove}
                disabled={mover.isPending || !targetColuna}
              >
                <Move size={14} strokeWidth={1.5} />
                {mover.isPending ? 'Movendo…' : 'Mover'}
              </Button>
            </div>
          </>
        ) : null
      }
    >
      {isLoading && <p className="text-sm text-text-muted">Carregando processo…</p>}
      {error && (
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar processo.'}
        </p>
      )}

      {data && summary && (
        <Stack gap="md">
          {/* Status chips no topo: fase atual + meta (cartório, protocolo, etc) */}
          <div className="flex items-center gap-2 flex-wrap">
            {colunaAtual && !(dominio === 'credito' && pipe === 'triagem_credito') && (
              <Chip tone="accent">
                {colunaAtual.nome}
              </Chip>
            )}
            {summary.chips
              ?.filter((c) => c.value)
              .map((c, i) => (
                <Chip key={`${c.label}-${i}`} tone={c.tone ?? 'neutral'} size="sm">
                  {c.label ? `${c.label}: ${c.value}` : c.value}
                </Chip>
              ))}
          </div>

          {mover.error && (
            <p className="text-xs text-danger">
              {mover.error instanceof ApiError ? mover.error.message : 'Falha ao mover'}
            </p>
          )}

          {dominio === 'credito' && pipe === 'cedulas_registro' && (
            <section className="rounded-lg border border-accent/30 bg-accent/5 p-4">
              <div className="mb-3 flex flex-col gap-1">
                <h3 className="text-sm font-semibold text-text">Anexar, conferir e atualizar a cédula</h3>
                <p className="text-xs text-text-muted">
                  Anexe a versão enviada ao cartório e toda nova versão corrigida. O Hub preservará o histórico, extrairá os dados para Relatórios &gt; Operações de Crédito e atualizará o acompanhamento do endividamento.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(220px,0.55fr)_auto] md:items-end">
                <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-xs font-medium text-text">
                  Arquivo da cédula <span className="text-danger">*</span>
                  <input
                    type="file"
                    required
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(event) => setCedulaExtractFile(event.target.files?.[0] ?? null)}
                    className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-xs text-text file:mr-3 file:rounded file:border-0 file:bg-surface2 file:px-2 file:py-1 file:text-xs file:text-text"
                  />
                  {cedulaExtractFile && <span className="text-text-muted">{cedulaExtractFile.name}</span>}
                </label>
                <label className="flex flex-col gap-1.5 text-xs font-medium text-text">
                  Etapa da versão
                  <select
                    value={cedulaEtapa}
                    onChange={(event) => setCedulaEtapa(event.target.value as typeof cedulaEtapa)}
                    className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-text"
                  >
                    <option value="enviada_cartorio">Enviada ao cartório</option>
                    <option value="corrigida">Corrigida / reemitida</option>
                    <option value="registrada">Registrada / devolvida</option>
                  </select>
                </label>
                <Button
                  variant="primary"
                  onClick={() => cedulaExtraction.mutate()}
                  disabled={!cedulaExtractFile || cedulaExtraction.isPending}
                >
                  <Paperclip size={14} strokeWidth={1.5} />
                  {cedulaExtraction.isPending ? 'Extraindo dados…' : 'Anexar e atualizar'}
                </Button>
              </div>
              {cedulaExtraction.error && (
                <p className="mt-3 text-xs text-danger">
                  {cedulaExtraction.error instanceof ApiError
                    ? cedulaExtraction.error.message
                    : 'Falha ao extrair a cédula.'}
                </p>
              )}
              {cedulaExtraction.data && (
                <p className="mt-3 text-xs text-success">
                  CARD atualizado. Endividamento: {cedulaExtraction.data.operacao_endividamento.status}
                  {cedulaExtraction.data.operacao_endividamento.campos_pendentes.length > 0
                    ? ` — pendente: ${cedulaExtraction.data.operacao_endividamento.campos_pendentes.join(', ')}`
                    : ''}.
                </p>
              )}
            </section>
          )}
          {comentariosEnabled && cardId && !hasComentariosTab && !(dominio === 'credito' && pipe === 'bb') && (
            <ComentariosSection dominio={dominio} cardId={cardId} />
          )}

          {dominio === 'ambiental' && cardId && Array.isArray(card.checklist_docs) && card.checklist_docs.length > 0 && (
            <AmbientalChecklistSection
              cardId={cardId}
              processType={typeof card.tipo_processo === 'string' ? card.tipo_processo : null}
              checklist={card.checklist_docs as ChecklistItem[]}
            />
          )}

          {/* Seções no padrão CRM — quando há tabs, agrupadas em abas */}
          {(() => {
            const allVisible = fields.filter((f) => f.alwaysShow || hasValue(f.value));
            if (tabs && tabs.length && cardId) {
              return (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    {tabs.map((t) => (
                      <TabsTrigger key={t.id} value={t.id}>
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {tabs.map((t) => {
                    const tabFields = allVisible.filter((f) => f.tab === t.id);
                    return (
                      <TabsContent key={t.id} value={t.id}>
                        {t.slot ? (
                          t.slot(cardId)
                        ) : (
                          <>
                            {renderFieldGroups(tabFields)}
                            {t.appendSlot && t.appendSlot(cardId)}
                          </>
                        )}
                      </TabsContent>
                    );
                  })}
                </Tabs>
              );
            }
            return renderFieldGroups(allVisible);
          })()}

        </Stack>
      )}
    </Modal>
  );
}

type ChecklistItem = {
  id: string;
  nome: string;
  status?: string | null;
  obrigatorio?: boolean | null;
  data_recebido?: string | null;
  validade?: string | null;
};

function AmbientalChecklistSection({
  cardId,
  processType,
  checklist,
}: {
  cardId: string;
  processType: string | null;
  checklist: ChecklistItem[];
}) {
  const qc = useQueryClient();
  const [openGroup, setOpenGroup] = useState<string | null | undefined>(undefined);
  const mutation = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: string }) =>
      api(`/api/v2/processos-ambiental/${cardId}/checklist/${encodeURIComponent(itemId)}`, {
        method: 'PATCH',
        body: { status },
        schema: z.unknown(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kanban', 'ambiental', 'detail', cardId] });
      void qc.invalidateQueries({ queryKey: ['kanban', 'ambiental'] });
    },
  });

  const groupedChecklist = groupAmbientalChecklist(processType, checklist);
  const totalDocs = checklist.length;
  const recebidos = checklist.filter((item) => item.status === 'recebido').length;
  const dispensados = checklist.filter((item) => item.status === 'dispensado').length;
  const pendentes = checklist.filter(
    (item) => !['recebido', 'dispensado'].includes(String(item.status || 'pendente')),
  ).length;
  const percentual = totalDocs > 0 ? Math.round((recebidos / totalDocs) * 100) : 0;

  useEffect(() => {
    if (!groupedChecklist.length) {
      setOpenGroup(null);
      return;
    }
    setOpenGroup((current) => {
      if (current === null) return current;
      if (current && groupedChecklist.some((group) => group.label === current)) return current;
      return groupedChecklist[0]?.label ?? null;
    });
  }, [groupedChecklist]);

  function toggleChecklistItem(item: ChecklistItem) {
    const nextStatus =
      item.status === 'recebido' ? 'pendente'
      : item.status === 'dispensado' ? 'pendente'
      : 'recebido';
    mutation.mutate({ itemId: item.id, status: nextStatus });
  }

  return (
    <section className="border border-border rounded-lg p-4 bg-surface-2">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          Checklist de Documentação
        </h3>
        <div className="text-xs text-text-muted">
          {totalDocs} docs · {recebidos} recebidos · {dispensados} dispensados · {pendentes} pendentes · {percentual}% concluído
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {groupedChecklist.map((group) => (
          <div key={group.label} className="rounded-xl border border-border bg-surface overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenGroup((current) => (current === group.label ? null : group.label))}
              className="flex w-full items-center justify-between border-b border-border/80 bg-surface-2 px-4 py-2 text-left"
            >
              <p className="text-[0.72rem] font-semibold uppercase tracking-wide text-text-muted">
                {group.label}
              </p>
              <ChevronRight
                size={16}
                strokeWidth={1.5}
                className="text-text-muted transition-transform"
                style={openGroup === group.label ? { transform: 'rotate(90deg)' } : undefined}
              />
            </button>
            {openGroup === group.label && (
              <div className="flex flex-col">
                {group.items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`px-3 py-3 ${index > 0 ? 'border-t border-border/70' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleChecklistItem(item)}
                      disabled={mutation.isPending}
                      className={`w-full text-left rounded-lg transition-colors ${
                        item.status === 'recebido'
                          ? 'bg-[rgba(76,175,80,0.06)]'
                          : item.status === 'dispensado'
                            ? 'bg-surface-2/70'
                          : 'hover:bg-surface-2/80'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`shrink-0 ${
                            item.status === 'recebido'
                              ? 'text-[var(--ok)]'
                              : item.status === 'dispensado'
                                ? 'text-text-muted'
                                : 'text-text-muted'
                          }`}
                        >
                          {item.status === 'recebido' ? (
                            <CheckSquare size={18} strokeWidth={1.8} />
                          ) : item.status === 'dispensado' ? (
                            <MinusSquare size={18} strokeWidth={1.8} />
                          ) : (
                            <Square size={18} strokeWidth={1.8} />
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-text">{item.nome}</p>
                          {item.status === 'dispensado' && (
                            <p className="text-xs text-text-muted">Dispensado para este tipo de requerente.</p>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {mutation.error && (
        <p className="mt-2 text-xs text-danger">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao atualizar checklist.'}
        </p>
      )}
    </section>
  );
}

function groupAmbientalChecklist(
  processType: string | null,
  checklist: ChecklistItem[],
): Array<{ label: string; items: ChecklistItem[] }> {
  if (processType === 'ambiental_laudo_parecer') {
    const groups = [
      { label: 'Documentos Pessoais', matcher: (item: ChecklistItem) => ['doc_rg_cnh', 'doc_cpf'].includes(item.id) },
      { label: 'Documentos do Imóvel', matcher: (item: ChecklistItem) => ['doc_cro_pgt'].includes(item.id) },
      { label: 'CAR', matcher: (item: ChecklistItem) => ['doc_car_recibo', 'doc_car_historico'].includes(item.id) },
      { label: 'GEO', matcher: (item: ChecklistItem) => ['doc_geo_shape', 'doc_geo_planta', 'doc_geo_memorial'].includes(item.id) },
      { label: 'Processo', matcher: (item: ChecklistItem) => ['doc_processo_pecas'].includes(item.id) },
      { label: 'Peça Técnica', matcher: (item: ChecklistItem) => ['minuta_peca', 'versao_final'].includes(item.id) },
    ];

    const grouped = groups
      .map((group) => ({
        label: group.label,
        items: checklist.filter(group.matcher),
      }))
      .filter((group) => group.items.length > 0);

    const usedIds = new Set(grouped.flatMap((group) => group.items.map((item) => item.id)));
    const remaining = checklist.filter((item) => !usedIds.has(item.id));
    if (remaining.length > 0) grouped.push({ label: 'Outros', items: remaining });
    return grouped;
  }

  return [{ label: 'Itens do Checklist', items: checklist }];
}

interface EditableProps {
  field: string;
  type: DetailFieldType;
  rawValue: string | number | boolean | string[] | null;
  displayValue: React.ReactNode;
  options?: Array<{ value: string; label: string }>;
  onSave: (payload: Record<string, unknown>) => void;
  saving: boolean;
}

interface EmitirOrcamentoFieldProps {
  clienteCpf: string | null;
  onOpen: () => void;
}

function TriagemAceiteField({
  cardId,
  pipe,
  assignedId,
  nome,
  status,
}: {
  cardId: string;
  pipe?: string;
  assignedId: number;
  nome: string | null;
  status: 'pendente' | 'aceito' | null;
}) {
  const qc = useQueryClient();
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
  const mutation = useMutation({
    mutationFn: ({ action, justificativa }: { action: 'aceitar' | 'recusar'; justificativa?: string }) =>
      api(`/api/v2/processos-credito/${cardId}/triagem/assistente`, {
        method: 'POST',
        body: { action, justificativa },
        schema: z.record(z.unknown()),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kanban', 'credito', 'detail', cardId] });
      void qc.invalidateQueries({ queryKey: ['kanban', 'credito', pipe ?? 'default'] });
    },
  });
  const role = String(me?.role || '').toLowerCase();
  const autorizado = Number(me?.id || 0) === assignedId || role === 'admin' || role === 'gestor';

  if (status === 'aceito') {
    return <div className="flex items-center gap-2"><span className="text-text">{nome || 'Assistente'}</span><Chip tone="ok" size="sm">Aceite confirmado</Chip></div>;
  }

  function recusar() {
    const justificativa = window.prompt('Informe a justificativa da recusa:')?.trim();
    if (!justificativa) return;
    mutation.mutate({ action: 'recusar', justificativa });
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-text">{nome || 'Assistente não definida'}</span>
        <Chip tone="warn" size="sm">Aguardando aceite</Chip>
      </div>
      {autorizado ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" size="sm" disabled={mutation.isPending || !cardId} onClick={() => mutation.mutate({ action: 'aceitar' })}>
            {mutation.isPending ? 'Registrando…' : 'Aceitar operação'}
          </Button>
          <Button variant="ghost" size="sm" disabled={mutation.isPending || !cardId} onClick={recusar}>Recusar e redistribuir</Button>
        </div>
      ) : (
        <span className="text-xs text-text-muted">O aceite deve ser feito pela assistente sugerida ou pela gestão.</span>
      )}
      {mutation.error && <span className="text-xs text-danger">{mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao registrar o aceite.'}</span>}
    </div>
  );
}

function EmitirOrcamentoField({ clienteCpf, onOpen }: EmitirOrcamentoFieldProps) {
  if (!clienteCpf) {
    return <span className="text-text-muted">Cliente sem CPF vinculado ao card.</span>;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-text-muted">
        Abra a proposta comercial deste cliente e gere o PDF a partir do cadastro vinculado.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={onOpen}>
          Emitir orçamento
        </Button>
        <span className="text-xs text-text-muted">{clienteCpf}</span>
      </div>
    </div>
  );
}

interface EmitirFormularioPrioridadeFieldProps {
  cardId: string;
  prioridadeAtiva: boolean;
}

function EmitirFormularioPrioridadeField({
  cardId,
  prioridadeAtiva,
}: EmitirFormularioPrioridadeFieldProps) {
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-ambiental/${cardId}/emitir-formulario-prioridade`, {
        method: 'POST',
        body: {},
        schema: z.object({
          ok: z.boolean().optional(),
          documento_id: z.union([z.coerce.number(), z.string()]).nullish(),
          arquivo_url: z.string().nullish(),
        }),
      }),
    onSuccess: (res) => {
      if (res?.arquivo_url) {
        window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  if (!prioridadeAtiva) {
    return <span className="text-text-muted">Marque a prioridade como sim para emitir o formulário.</span>;
  }

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: 'rgba(220, 38, 38, 0.22)',
        background: 'rgba(220, 38, 38, 0.06)',
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="danger" size="sm">
            Prioridade ativa
          </Chip>
          <span className="text-sm text-text">
            Pedido de análise prioritária marcado como sim.
          </span>
        </div>
        <p className="text-sm text-text-muted">
          Gere e abra o requerimento padrão da SEDAM para este processo de CAR.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Emitindo...' : 'Emitir formulário'}
          </Button>
          {mutation.isSuccess && (
            <span className="text-xs text-[var(--ok)]">Formulário pronto para abrir.</span>
          )}
        </div>
        {mutation.error && (
          <span className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao emitir o formulário.'}
          </span>
        )}
      </div>
    </div>
  );
}

interface EmitirFormularioPraFieldProps {
  cardId: string;
}

function EmitirFormularioPraField({ cardId }: EmitirFormularioPraFieldProps) {
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-ambiental/${cardId}/emitir-formulario-pra`, {
        method: 'POST',
        body: {},
        schema: z.object({
          ok: z.boolean().optional(),
          documento_id: z.union([z.coerce.number(), z.string()]).nullish(),
          arquivo_url: z.string().nullish(),
        }),
      }),
    onSuccess: (res) => {
      if (res?.arquivo_url) {
        window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: 'rgba(34, 139, 34, 0.22)',
        background: 'rgba(34, 139, 34, 0.06)',
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="ok" size="sm">
            Modelo oficial
          </Chip>
          <span className="text-sm text-text">
            Gere o requerimento de adesão ao PRA a partir do layout oficial da SEDAM.
          </span>
        </div>
        <p className="text-sm text-text-muted">
          O documento é emitido com os dados já vinculados ao cliente e ao imóvel deste processo.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Emitindo...' : 'Emitir formulário'}
          </Button>
          {mutation.isSuccess && (
            <span className="text-xs text-[var(--ok)]">Formulário pronto para abrir.</span>
          )}
        </div>
        {mutation.error && (
          <span className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao emitir o formulário.'}
          </span>
        )}
      </div>
    </div>
  );
}

interface EmitirFormularioAlteracaoEmailSicarFieldProps {
  cardId: string;
}

function EmitirFormularioAlteracaoEmailSicarField({ cardId }: EmitirFormularioAlteracaoEmailSicarFieldProps) {
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-ambiental/${cardId}/emitir-formulario-alteracao-email-sicar`, {
        method: 'POST',
        body: {},
        schema: z.object({
          ok: z.boolean().optional(),
          documento_id: z.union([z.coerce.number(), z.string()]).nullish(),
          arquivo_url: z.string().nullish(),
        }),
      }),
    onSuccess: (res) => {
      if (res?.arquivo_url) {
        window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: 'rgba(59, 130, 246, 0.22)',
        background: 'rgba(59, 130, 246, 0.06)',
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="info" size="sm">
            Modelo oficial
          </Chip>
          <span className="text-sm text-text">
            Gere o requerimento oficial de alteração de e-mail no SICAR preservando o layout da SEDAM.
          </span>
        </div>
        <p className="text-sm text-text-muted">
          Preencha antes o campo do novo e-mail no SICAR. O formulário usa o template oficial de uma página, sem alterar a estrutura do órgão.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Emitindo...' : 'Emitir formulário'}
          </Button>
          {mutation.isSuccess && (
            <span className="text-xs text-[var(--ok)]">Formulário pronto para abrir.</span>
          )}
        </div>
        {mutation.error && (
          <span className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao emitir o formulário.'}
          </span>
        )}
      </div>
    </div>
  );
}

interface EmitirFormularioCancelamentoCarFieldProps {
  cardId: string;
}

function EmitirFormularioCancelamentoCarField({ cardId }: EmitirFormularioCancelamentoCarFieldProps) {
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-ambiental/${cardId}/emitir-formulario-cancelamento-car`, {
        method: 'POST',
        body: {},
        schema: z.object({
          ok: z.boolean().optional(),
          documento_id: z.union([z.coerce.number(), z.string()]).nullish(),
          arquivo_url: z.string().nullish(),
        }),
      }),
    onSuccess: (res) => {
      if (res?.arquivo_url) {
        window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: 'rgba(180, 83, 9, 0.22)',
        background: 'rgba(180, 83, 9, 0.06)',
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="warn" size="sm">
            Modelo oficial
          </Chip>
          <span className="text-sm text-text">
            Gere o requerimento oficial de cancelamento de CAR preservando o layout da SEDAM.
          </span>
        </div>
        <p className="text-sm text-text-muted">
          O documento usa o template oficial de duas páginas. Preencha antes o motivo do cancelamento e, se houver, os dados da unificação.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Emitindo...' : 'Emitir formulário'}
          </Button>
          {mutation.isSuccess && (
            <span className="text-xs text-[var(--ok)]">Formulário pronto para abrir.</span>
          )}
        </div>
        {mutation.error && (
          <span className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao emitir o formulário.'}
          </span>
        )}
      </div>
    </div>
  );
}

interface EmitirPradaSimplificadoFieldProps {
  cardId: string;
}

function EmitirPradaSimplificadoField({ cardId }: EmitirPradaSimplificadoFieldProps) {
  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-ambiental/${cardId}/emitir-prada-simplificado`, {
        method: 'POST',
        body: {},
        schema: z.object({
          ok: z.boolean().optional(),
          documento_id: z.union([z.coerce.number(), z.string()]).nullish(),
          arquivo_url: z.string().nullish(),
        }),
      }),
    onSuccess: (res) => {
      if (res?.arquivo_url) {
        window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: 'rgba(34, 139, 34, 0.22)',
        background: 'rgba(34, 139, 34, 0.06)',
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="ok" size="sm">
            Modelo oficial
          </Chip>
          <span className="text-sm text-text">
            Gere o PRADA simplificado usando o template oficial em branco, preservando o layout do órgão.
          </span>
        </div>
        <p className="text-sm text-text-muted">
          A emissão usa somente os dados confirmados no processo. Os campos não preenchidos permanecem em branco no formulário oficial.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Emitindo...' : 'Emitir PRADA simplificado'}
          </Button>
          {mutation.isSuccess && (
            <span className="text-xs text-[var(--ok)]">Documento pronto para abrir.</span>
          )}
        </div>
        {mutation.error && (
          <span className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao emitir o PRADA simplificado.'}
          </span>
        )}
      </div>
    </div>
  );
}

function selectedList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function proposalEscape(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

const PROPOSTA_CONFIDENCIALIDADE =
  'Todas as informações, documentos e dados trocados entre a RURALPLAN e o contratante serão tratados como confidenciais e utilizados exclusivamente para avaliação, contratação e execução dos serviços desta proposta. O contratante compromete-se a não reproduzir, compartilhar, encaminhar, divulgar ou apresentar esta proposta, total ou parcialmente, inclusive preços, condições comerciais, metodologia, documentos e informações da RURALPLAN, a quaisquer terceiros sem autorização prévia e escrita da RURALPLAN, ressalvadas as divulgações exigidas por lei ou por determinação de autoridade competente. A obrigação de confidencialidade permanece válida mesmo que a proposta não seja aceita.';

const PROPOSTA_ACEITE_CONTRATO =
  'Declaro estar de acordo com os serviços, valores, condições comerciais e condições gerais desta proposta, autorizando a RURALPLAN a emitir o respectivo contrato de prestação de serviços.';

const PROPOSTA_CONDICOES_GERAIS = [
  'Esta proposta contempla a legislação atual. Caso haja outras exigências em função de novas legislações, deverá ser objeto de novo contrato.',
  'Poderá ser elaborado memorial descritivo da indicação em planta de toda e qualquer servidão existente no imóvel rural, para o seu devido registro em cartório.',
  'As questões relacionadas à discordância de divisas entre imóveis rurais lindeiros, não resolvidas pela análise dos documentos existentes e pela prática do bom senso, e que dependam de mediação jurídica, deverão ser objeto de novo contrato.',
  'A alteração ou atualização cadastral na Receita Federal do Brasil (ITR), no INCRA (CCIR) e na SEDAM (CAR) deverá ser contratada separadamente.',
  'É vedada a divulgação, a qualquer tempo e sob qualquer forma ou natureza, de dados e informações confidenciais presentes nesta proposta de prestação de serviços.',
  'Os levantamentos de campo deverão ser acompanhados por pessoa de confiança do contratante, conhecedora dos limites e das confrontações.',
  'O pagamento de taxas, despesas cartorárias e certidões de matrículas do Cartório de Registro de Imóveis não está incluído no valor desta proposta.',
  'Esta proposta tem validade de 10 dias.',
];

const EXTENSO_UNIDADES = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const EXTENSO_ESPECIAIS = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const EXTENSO_DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const EXTENSO_CENTENAS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

function grupoPorExtenso(valor: number): string {
  if (valor === 100) return 'cem';
  const partes: string[] = [];
  const centena = Math.floor(valor / 100);
  const resto = valor % 100;
  if (centena) partes.push(EXTENSO_CENTENAS[centena]!);
  if (resto) {
    if (partes.length) partes.push('e');
    if (resto < 10) partes.push(EXTENSO_UNIDADES[resto]!);
    else if (resto < 20) partes.push(EXTENSO_ESPECIAIS[resto - 10]!);
    else {
      partes.push(EXTENSO_DEZENAS[Math.floor(resto / 10)]!);
      if (resto % 10) partes.push('e', EXTENSO_UNIDADES[resto % 10]!);
    }
  }
  return partes.join(' ');
}

function inteiroPorExtenso(valor: number): string {
  if (valor === 0) return 'zero';
  const escalas = [
    { divisor: 1_000_000_000, singular: 'bilhão', plural: 'bilhões' },
    { divisor: 1_000_000, singular: 'milhão', plural: 'milhões' },
    { divisor: 1_000, singular: 'mil', plural: 'mil' },
    { divisor: 1, singular: '', plural: '' },
  ];
  let restante = valor;
  const partes: Array<{ texto: string; grupo: number }> = [];
  for (const escala of escalas) {
    const grupo = Math.floor(restante / escala.divisor);
    if (!grupo) continue;
    restante %= escala.divisor;
    const numero = escala.divisor === 1_000 && grupo === 1 ? '' : grupoPorExtenso(grupo);
    const nomeEscala = grupo === 1 ? escala.singular : escala.plural;
    partes.push({ texto: [numero, nomeEscala].filter(Boolean).join(' '), grupo });
  }
  return partes.map((parte, indice) => {
    if (indice === 0) return parte.texto;
    const ultima = indice === partes.length - 1;
    const separador = ultima && (parte.grupo < 100 || parte.grupo % 100 === 0) ? ' e ' : ' ';
    return `${separador}${parte.texto}`;
  }).join('');
}

function valorMonetarioPorExtenso(value: unknown): string | null {
  const texto = String(value ?? '').trim();
  const normalizado = texto.includes(',')
    ? texto.replace(/\./g, '').replace(',', '.')
    : texto;
  const numero = Number(normalizado);
  if (!Number.isFinite(numero) || numero < 0) return null;
  const centavosTotais = Math.round(numero * 100);
  const reais = Math.floor(centavosTotais / 100);
  const centavos = centavosTotais % 100;
  const partes: string[] = [];
  if (reais || !centavos) partes.push(`${inteiroPorExtenso(reais)} ${reais === 1 ? 'real' : 'reais'}`);
  if (centavos) partes.push(`${inteiroPorExtenso(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`);
  return partes.join(' e ');
}

const propostaVersaoSchema = z.object({
  id: z.coerce.number(),
  processo_id: z.coerce.number(),
  versao: z.coerce.number(),
  status: z.enum(['rascunho', 'aprovada']),
  emitida_em: z.string(),
  aprovada_em: z.string().nullable().optional(),
  reutilizada: z.boolean().optional(),
});

const propostaVersoesSchema = z.object({
  versoes: z.array(z.object({
    id: z.coerce.number(),
    processo_id: z.coerce.number(),
    versao: z.coerce.number(),
    status: z.enum(['rascunho', 'aprovada']),
    emitida_em: z.string(),
    aprovada_em: z.string().nullable().optional(),
    emitida_por_nome: z.string().nullable().optional(),
    aprovada_por_nome: z.string().nullable().optional(),
  })),
});

function EmitirPropostaFundiariaField({ card }: { card: Record<string, unknown> }) {
  const processoId = Number(card.id);
  const qc = useQueryClient();
  const [versionando, setVersionando] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [erroEmissao, setErroEmissao] = useState<string | null>(null);
  const [mensagemEmissao, setMensagemEmissao] = useState<string | null>(null);
  const versoesQuery = useQuery({
    queryKey: ['processos-geo', processoId, 'propostas'],
    queryFn: ({ signal }) => api(`/api/v2/processos-geo/${processoId}/propostas`, {
      signal,
      schema: propostaVersoesSchema,
    }),
    enabled: Number.isFinite(processoId) && processoId > 0,
  });
  const servicos = selectedList(card.servicos_orcados);
  const imoveisSelecionados = new Set(selectedList(card.imoveis_contrato));
  const outroSelecionado = imoveisSelecionados.has('__outro__');
  const outroImovel = outroSelecionado
    ? String(card.imoveis_contrato_outros ?? '').trim()
    : '';
  const imoveis = (Array.isArray(card.cliente_imoveis) ? card.cliente_imoveis : [])
    .filter((raw) => imoveisSelecionados.has(String((raw as Record<string, unknown>).id)))
    .map((raw) => raw as Record<string, unknown>);
  const pronta = servicos.length > 0
    && (imoveis.length > 0 || outroSelecionado)
    && (!outroSelecionado || Boolean(outroImovel));
  const pagamento: Record<string, string> = {
    avista: 'Pagamento à vista',
    entrada_50_restante_10_dias: 'Entrada de 50% e restante após 10 dias',
    entrada_50_restante_30_dias: 'Entrada de 50% e restante após 30 dias',
  };
  const valor = card.valor_total != null && card.valor_total !== ''
    ? Number(card.valor_total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'A definir';
  const valorExtenso = valorMonetarioPorExtenso(card.valor_total);
  const prazoRaw = String(card.data_prazo_entrega ?? '');
  const prazo = /^\d{4}-\d{2}-\d{2}/.test(prazoRaw)
    ? new Date(`${prazoRaw.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR')
    : (prazoRaw || 'A definir');
  const pagamentoDescricao = pagamento[String(card.plano_pagamento ?? '')]
    || String(card.plano_pagamento || 'A definir');
  const locaisSelecionados = Array.from(new Set(imoveis
    .map((imovel) => [imovel.municipio, imovel.uf].filter(Boolean).join('/'))
    .filter(Boolean)));
  const imovelResumo = card.imovel_resumo && typeof card.imovel_resumo === 'object'
    ? card.imovel_resumo as Record<string, unknown>
    : {};
  const localImovelPrincipal = [
    card.imovel_municipio || card.municipio || imovelResumo.municipio,
    card.imovel_uf || imovelResumo.uf,
  ].filter(Boolean).join('/');
  const localAssinatura = locaisSelecionados.length === 1
    ? locaisSelecionados[0]
    : (localImovelPrincipal || locaisSelecionados[0] || '________________________________');
  const snapshot = {
    proposta_id: processoId,
    cliente: {
      nome: String(card.cliente_nome || '—'),
      documento: String(card.cliente_cpf || '—'),
    },
    imoveis: imoveis.map((imovel) => ({
      denominacao: imovel.denominacao || null,
      matricula: imovel.matricula || null,
      municipio: imovel.municipio || null,
      uf: imovel.uf || null,
      area_total_ha: imovel.area_total_ha ?? null,
    })),
    outro_imovel: outroImovel || null,
    servicos,
    condicoes: {
      valor_total: valor,
      valor_total_extenso: valorExtenso,
      pagamento: pagamentoDescricao,
      prazo,
      gerais: PROPOSTA_CONDICOES_GERAIS,
    },
    confidencialidade: PROPOSTA_CONFIDENCIALIDADE,
    aceite_contrato: {
      texto: PROPOSTA_ACEITE_CONTRATO,
      rotulo_assinatura: 'Assinatura do cliente/contratante',
      local: localAssinatura,
    },
  };

  async function imprimir() {
    if (!pronta) return;
    const printWindow = window.open('', '_blank', 'width=900,height=760');
    if (!printWindow) {
      setErroEmissao('Permita a abertura da janela de impressão neste navegador.');
      return;
    }
    printWindow.opener = null;
    setVersionando(true);
    setErroEmissao(null);
    const servicosHtml = servicos.map((item) => `<li>${proposalEscape(item)}</li>`).join('');
    const condicoesGeraisHtml = PROPOSTA_CONDICOES_GERAIS
      .map((item) => `<li>${proposalEscape(item)}</li>`)
      .join('');
    const imoveisHtml = imoveis.map((imovel) => {
      const nome = imovel.denominacao || 'Imóvel rural';
      const matricula = imovel.matricula ? ` — Matrícula ${String(imovel.matricula)}` : '';
      const local = [imovel.municipio, imovel.uf].filter(Boolean).join('/');
      const area = imovel.area_total_ha != null ? ` — ${Number(imovel.area_total_ha).toLocaleString('pt-BR')} ha` : '';
      return `<li><strong>${proposalEscape(nome)}</strong>${proposalEscape(matricula)}${local ? ` — ${proposalEscape(local)}` : ''}${proposalEscape(area)}</li>`;
    }).join('') + (outroImovel
      ? `<li><strong>Outro imóvel:</strong> ${proposalEscape(outroImovel)}</li>`
      : '');
    try {
      const registro = await api(`/api/v2/processos-geo/${processoId}/propostas`, {
        method: 'POST',
        body: { snapshot },
        schema: propostaVersaoSchema,
      });
      void qc.invalidateQueries({ queryKey: ['processos-geo', processoId, 'propostas'] });
      setMensagemEmissao(registro.reutilizada
        ? `Versão v${String(registro.versao).padStart(2, '0')} reutilizada sem alteração.`
        : `Nova versão v${String(registro.versao).padStart(2, '0')} criada.`);
      const versaoLabel = `v${String(registro.versao).padStart(2, '0')}`;
      const statusLabel = registro.status === 'aprovada' ? 'Aprovada' : 'Aguardando aprovação';
      const dataEmissao = new Date(registro.emitida_em).toLocaleDateString('pt-BR');
      const logoUrl = `${window.location.origin}/logos/ruralplan-oficial.jpg`;
      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
      <html lang="pt-BR"><head><meta charset="utf-8"><title>Proposta Fundiária #${proposalEscape(card.id)} — ${versaoLabel}</title>
      <style>
        @page { size: A4; margin: 18mm; }
        * { box-sizing: border-box; }
        body { margin: 0; color: #2d302d; font: 14px/1.55 Arial, sans-serif; }
        header { border-bottom: 3px solid #ef6a47; margin-bottom: 28px; padding-bottom: 16px; }
        .brand-logo { display: block; height: auto; margin: 0 auto 0 0; max-width: 100%; width: 7cm; }
        h1 { font-size: 20px; margin: 8px 0 0; text-align: center; }
        h2 { color: #305d45; font-size: 14px; margin: 24px 0 8px; text-transform: uppercase; }
        .meta { display: grid; gap: 8px; grid-template-columns: 1fr 1fr; }
        .box { background: #f6f3ee; border-radius: 8px; padding: 12px 14px; }
        .label { color: #766f67; display: block; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
        ul { margin: 8px 0; padding-left: 22px; } li { margin: 6px 0; }
        .total { color: #305d45; font-size: 20px; font-weight: 800; }
        .valor-extenso { display: block; font-size: 11px; margin-top: 2px; }
        .confidencialidade { background: #f6f3ee; border-left: 3px solid #305d45; border-radius: 0 8px 8px 0; padding: 12px 14px; text-align: justify; }
        .aceite { break-inside: avoid; margin-top: 28px; page-break-inside: avoid; }
        .aceite p { margin: 0; text-align: justify; }
        .assinatura { margin: 58px auto 0; max-width: 420px; text-align: center; }
        .assinatura-linha { border-top: 1px solid #2d302d; padding-top: 6px; }
        .assinatura-data { margin-top: 20px; }
        footer { border-top: 1px solid #d9d4cd; color: #766f67; margin-top: 36px; padding-top: 14px; text-align: center; }
      </style></head><body>
      <header><img class="brand-logo" src="${proposalEscape(logoUrl)}" alt="RURALPLAN"><h1>Proposta de Prestação de Serviços Fundiários</h1></header>
      <div class="meta">
        <div class="box"><span class="label">Cliente</span>${proposalEscape(card.cliente_nome || '—')}</div>
        <div class="box"><span class="label">CPF/CNPJ</span>${proposalEscape(card.cliente_cpf || '—')}</div>
        <div class="box"><span class="label">Proposta</span>#${proposalEscape(card.id || '—')}</div>
        <div class="box"><span class="label">Versão</span>${versaoLabel}</div>
        <div class="box"><span class="label">Status</span>${statusLabel}</div>
        <div class="box"><span class="label">Data de emissão</span>${dataEmissao}</div>
      </div>
      <h2>Imóveis abrangidos</h2><ul>${imoveisHtml}</ul>
      <h2>Serviços propostos</h2><ul>${servicosHtml}</ul>
      <h2>Condições comerciais</h2>
      <div class="meta">
        <div class="box"><span class="label">Valor total</span><span class="total">${proposalEscape(valor)}</span>${valorExtenso ? `<span class="valor-extenso">(${proposalEscape(valorExtenso)})</span>` : ''}</div>
        <div class="box"><span class="label">Condição de pagamento</span>${proposalEscape(pagamentoDescricao)}</div>
        <div class="box"><span class="label">Prazo previsto de execução</span>${proposalEscape(prazo)}</div>
      </div>
      <h2>Das condições gerais</h2>
      <ul>${condicoesGeraisHtml}</ul>
      <h2>Confidencialidade</h2>
      <div class="confidencialidade">${proposalEscape(PROPOSTA_CONFIDENCIALIDADE)}</div>
      <section class="aceite">
        <h2>De acordo do cliente para emissão do contrato</h2>
        <p>${proposalEscape(PROPOSTA_ACEITE_CONTRATO)}</p>
        <div class="assinatura">
          <div class="assinatura-linha">${proposalEscape(card.cliente_nome || 'Assinatura do cliente/contratante')}</div>
          <div class="assinatura-data">Local e data: ${proposalEscape(localAssinatura)}, ${proposalEscape(dataEmissao)}</div>
        </div>
      </section>
      <footer>RURALPLAN | 360º em soluções para o Agronegócio</footer>
      </body></html>`);
      printWindow.document.close();
      const imprimirJanela = () => {
        printWindow.focus();
        printWindow.print();
      };
      const logo = printWindow.document.querySelector('img');
      if (logo && !logo.complete) {
        logo.onload = imprimirJanela;
        logo.onerror = imprimirJanela;
      } else {
        window.setTimeout(imprimirJanela, 250);
      }
    } catch (error) {
      printWindow.close();
      setErroEmissao(error instanceof ApiError ? error.message : 'Não foi possível versionar a proposta.');
    } finally {
      setVersionando(false);
    }
  }

  const ultimaVersao = versoesQuery.data?.versoes[0];

  async function aprovarUltimaVersao() {
    if (!ultimaVersao || ultimaVersao.status === 'aprovada') return;
    const confirmou = window.confirm(
      `Aprovar a versão v${String(ultimaVersao.versao).padStart(2, '0')}? Após a aprovação, essa versão ficará imutável.`,
    );
    if (!confirmou) return;
    setAprovando(true);
    setErroEmissao(null);
    setMensagemEmissao(null);
    try {
      await api(`/api/v2/processos-geo/${processoId}/propostas/${ultimaVersao.versao}/aprovar`, {
        method: 'POST',
        body: { snapshot },
        schema: propostaVersaoSchema,
      });
      setMensagemEmissao(`Versão v${String(ultimaVersao.versao).padStart(2, '0')} aprovada e bloqueada para alterações.`);
      await qc.invalidateQueries({ queryKey: ['processos-geo', processoId, 'propostas'] });
    } catch (error) {
      setErroEmissao(error instanceof ApiError ? error.message : 'Não foi possível aprovar a proposta.');
    } finally {
      setAprovando(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-border bg-surface2 p-3">
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => void imprimir()} disabled={!pronta || versionando || aprovando}>
          {versionando ? 'Verificando versão…' : 'Visualizar / imprimir proposta'}
        </Button>
        {ultimaVersao && ultimaVersao.status !== 'aprovada' ? (
          <Button variant="ghost" onClick={() => void aprovarUltimaVersao()} disabled={!pronta || versionando || aprovando}>
            {aprovando ? 'Aprovando…' : `Aprovar v${String(ultimaVersao.versao).padStart(2, '0')}`}
          </Button>
        ) : null}
      </div>
      <span className="text-xs text-text-muted">
        {pronta
          ? ultimaVersao
            ? ultimaVersao.status === 'aprovada'
              ? `Última versão: v${String(ultimaVersao.versao).padStart(2, '0')} — aprovada e imutável.`
              : `Última versão: v${String(ultimaVersao.versao).padStart(2, '0')} — aguardando aprovação. Uma nova versão só será criada se os dados forem alterados.`
            : 'A primeira emissão será registrada como v01. Impressões sem alteração reutilizarão a mesma versão.'
          : outroSelecionado && !outroImovel
            ? 'Preencha a identificação do outro imóvel para liberar a impressão.'
            : 'Selecione ao menos um serviço e um imóvel para liberar a impressão.'}
      </span>
      {mensagemEmissao ? <span className="text-xs text-success">{mensagemEmissao}</span> : null}
      {erroEmissao ? <span className="text-xs text-danger">{erroEmissao}</span> : null}
    </div>
  );
}

// Click-to-edit + auto-save no blur. Mantém o display limpo até o usuário focar.
function EditableField({
  field,
  type,
  rawValue,
  displayValue,
  options,
  onSave,
  saving,
}: EditableProps) {
  const [editing, setEditing] = useState(false);
  const initial = useRef(rawValue);
  const [draft, setDraft] = useState<string>(formatRaw(rawValue, type));
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Quando o cache atualiza (PATCH bem-sucedido), reseta initial
  useEffect(() => {
    if (!editing) {
      initial.current = rawValue;
      setDraft(formatRaw(rawValue, type));
    }
  }, [rawValue, editing, type]);

  function commit(value: string | boolean) {
    setEditing(false);
    const normalized = normalize(value, type);
    if (normalized === initial.current) return;
    onSave({ [field]: normalized });
    setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 1500);
  }

  if (type === 'multiselect' && options) {
    let selected: string[] = [];
    if (Array.isArray(rawValue)) selected = rawValue.map(String);
    else if (typeof rawValue === 'string' && rawValue.trim()) {
      try {
        const parsed: unknown = JSON.parse(rawValue);
        if (Array.isArray(parsed)) selected = parsed.map(String);
      } catch {
        selected = [];
      }
    }
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface2 p-3">
        {options.length === 0 ? (
          <span className="text-sm text-text-muted">Nenhuma opção disponível.</span>
        ) : options.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={checked}
                disabled={saving}
                onChange={() => {
                  const next = checked
                    ? selected.filter((value) => value !== option.value)
                    : [...selected, option.value];
                  onSave({ [field]: next });
                }}
                className="mt-0.5 rounded border-border2"
              />
              <span>{option.label}</span>
            </label>
          );
        })}
        {saving && <span className="text-[0.7rem] text-text-muted">salvando…</span>}
      </div>
    );
  }

  if (type === 'boolean') {
    const cur = rawValue === true || rawValue === 'true' || rawValue === 't';
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={cur}
          disabled={saving}
          onChange={(e) => onSave({ [field]: e.target.checked })}
          className="rounded border-border2"
        />
        <span className="text-text">{cur ? 'Sim' : 'Não'}</span>
        {savedAt && <span className="text-[0.7rem] text-[var(--ok)]">✓</span>}
      </label>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-left w-full hover:bg-surface2 hover:text-text rounded px-1.5 -mx-1.5 py-0.5 transition-colors text-text"
        style={{ minHeight: '1.5rem' }}
      >
        <span className={rawValue == null || rawValue === '' ? 'text-text-muted' : ''}>
          {displayValue}
        </span>
        {savedAt && <span className="ml-2 text-[0.7rem] text-[var(--ok)]">✓ salvo</span>}
      </button>
    );
  }

  if (type === 'select' && options) {
    return (
      <select
        autoFocus
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          commit(next);
        }}
        onBlur={() => setEditing(false)}
        className="w-full rounded-md bg-elevated border-[1.5px] border-accent px-2 py-1 text-sm text-text outline-none"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        rows={3}
        className="w-full rounded-md bg-elevated border-[1.5px] border-accent px-2 py-1 text-sm text-text outline-none resize-none"
      />
    );
  }

  const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';
  return (
    <input
      autoFocus
      type={inputType}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(formatRaw(initial.current, type));
          setEditing(false);
        }
      }}
      className="w-full rounded-md bg-elevated border-[1.5px] border-accent px-2 py-1 text-sm text-text outline-none"
    />
  );
}

// ─── Comentários ─────────────────────────────────────────────────────────
interface ComentariosProps {
  dominio: Dominio;
  cardId: string;
}

function fmtKB(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ComentariosSection({ dominio, cardId }: ComentariosProps) {
  const { data: comentarios = [], isLoading } = useComentarios(dominio, cardId);
  const addCom = useAddComentario(dominio, cardId);
  const uploadSupported = hasComentariosUploadSupport(dominio);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setOpen(false);
  }, [cardId]);

  function submit() {
    const txt = draft.trim();
    if (!txt && pending.length === 0) return;
    addCom.mutate(
      { texto: txt, files: pending },
      {
        onSuccess: () => {
          setDraft('');
          setPending([]);
          if (fileRef.current) fileRef.current.value = '';
        },
      },
    );
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setPending((cur) => [...cur, ...files]);
  }

  function removePending(idx: number) {
    setPending((cur) => cur.filter((_, i) => i !== idx));
  }

  return (
    <section className="border border-border rounded-lg p-4 bg-surface-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <span className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          Comentários
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-text-muted">
          {isLoading ? 'Carregando…' : `${comentarios.length} comentário(s)`}
          <ChevronRight
            size={16}
            strokeWidth={1.5}
            className="transition-transform"
            style={open ? { transform: 'rotate(90deg)' } : undefined}
          />
        </span>
      </button>
      {!open ? null : (
        <div className="mt-3">
      <div className="flex flex-col gap-2 mb-3">
        {isLoading ? (
          <p className="text-xs text-text-muted">Carregando…</p>
        ) : comentarios.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhum comentário ainda.</p>
        ) : (
          comentarios.map((c) => (
            <div
              key={c.id}
              className="border border-border rounded-md px-3 py-2 bg-elevated"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[0.78rem] font-semibold text-text">
                  {c.usuario_nome ?? `#${c.usuario_id ?? '—'}`}
                </span>
                <span className="text-[0.7rem] text-text-muted">
                  {new Date(c.created_at).toLocaleString('pt-BR')}
                </span>
              </div>
              {c.texto && (
                <p className="text-sm text-text whitespace-pre-wrap break-words">{c.texto}</p>
              )}
              {Array.isArray(c.anexos) && c.anexos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.anexos.map((a, idx) => (
                    <a
                      key={`${c.id}-${idx}`}
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 max-w-[220px] px-2 py-1 rounded-md border border-border bg-surface text-[0.72rem] text-text-2 hover:border-accent transition-colors"
                      title={a.nome}
                    >
                      <Paperclip size={12} strokeWidth={1.5} />
                      <span className="truncate">{a.nome}</span>
                      {a.tamanho != null && (
                        <span className="text-text-muted shrink-0 text-[0.65rem]">
                          {fmtKB(a.tamanho)}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <div className="flex flex-col gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Escreva um comentário…"
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text placeholder:text-text-muted hover:border-[var(--text-muted)] focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)] resize-none"
        />
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pending.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-accent bg-elevated text-[0.72rem] text-text-2"
              >
                <Paperclip size={12} strokeWidth={1.5} />
                <span className="max-w-[140px] truncate">{f.name}</span>
                <span className="text-text-muted text-[0.65rem]">{fmtKB(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removePending(i)}
                  className="text-text-muted hover:text-danger leading-none"
                  aria-label={`Remover ${f.name}`}
                >
                  <X size={12} strokeWidth={1.5} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          {uploadSupported && (
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx"
              className="hidden"
              onChange={onPickFiles}
            />
          )}
          {uploadSupported ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={addCom.isPending}
            >
              <Paperclip size={14} strokeWidth={1.5} />
              Anexar
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={addCom.isPending || (!draft.trim() && pending.length === 0)}
          >
            {addCom.isPending ? 'Enviando…' : 'Comentar'}
          </Button>
        </div>
      </div>
        </div>
      )}
    </section>
  );
}

interface CollapsibleProps {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
  defaultOpen?: boolean;
}

// Card de fase anterior — fechado por padrão, abre ao clicar.
// Mesmo padrão visual do FaseAnteriorCard do CRM.
function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false }: CollapsibleProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl bg-elevated mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        {Icon && (
          <span
            className="inline-flex items-center justify-center rounded-md shrink-0"
            style={{
              width: 28,
              height: 28,
              background: 'rgba(217,119,87,0.10)',
              color: '#d97757',
            }}
          >
            <Icon size={15} strokeWidth={1.75} />
          </span>
        )}
        <span className="flex-1 text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          {title}
        </span>
        <span className="text-xs text-text-muted hidden sm:inline">
          {open ? 'Mostrar menos' : 'Ver mais'}
        </span>
        <ChevronRight
          size={16}
          strokeWidth={1.5}
          className="text-text-muted transition-transform"
          style={open ? { transform: 'rotate(90deg)' } : undefined}
        />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-border">{children}</div>}
    </div>
  );
}

// Divisor com label. accent=true destaca em coral (usado pra "Fase atual"),
// neutro pra "Fases anteriores".
function SectionDivider({ label, accent }: { label: string; accent?: boolean }) {
  return (
    <div className="flex items-center gap-3 my-3">
      <span
        className="text-[0.7rem] font-semibold uppercase tracking-wide shrink-0"
        style={{ color: accent ? '#d97757' : 'var(--text-muted)' }}
      >
        {label}
      </span>
      <span
        className="flex-1 h-px"
        style={{
          background: accent ? 'rgba(217,119,87,0.25)' : 'var(--border)',
        }}
      />
    </div>
  );
}

// Mapeia título de seção pra ícone Lucide. Cobre as fases dos 3 pipes
// fundiários (GEO, CCIR, Titulação) + meta.
function sectionIcon(title: string): LucideIcon | null {
  const stripped = title.replace(/^\d+\.\s*/, '').toLowerCase();
  if (/dados principais/.test(stripped)) return User;
  if (/entrada/.test(stripped)) return Inbox;
  if (/documenta/.test(stripped)) return FolderOpen;
  if (/campo/.test(stripped)) return MapPin;
  if (/sigef/.test(stripped)) return SatelliteDish;
  if (/montagem/.test(stripped)) return Archive;
  if (/cart[oó]rio/.test(stripped)) return Landmark;
  if (/conclu/.test(stripped)) return CircleCheck;
  // CCIR / Titulação específicos
  if (/execu[çc][aã]o\s+incra|protocolo\s+pgt/.test(stripped)) return SatelliteDish;
  if (/aguardando\s+an[aá]lise/.test(stripped)) return Inbox;
  if (/vistoria|cro/.test(stripped)) return MapPin;
  if (/emiss[aã]o|entrega/.test(stripped)) return CircleCheck;
  if (/outros/.test(stripped)) return MoreHorizontal;
  if (/^[a-z]/.test(stripped)) return Layers;
  return null;
}

interface ResponsaveisProps {
  current: UserRef[];
  editable: boolean;
  saving: boolean;
  onSave: (userIds: number[], motivo: string) => void;
}

// Lista de chips de responsáveis com remoção e adição via select.
// Cada mudança dispara PUT com a nova lista completa.
function ResponsaveisEditor({ current, editable, saving, onSave }: ResponsaveisProps) {
  const usersQ = useUsers();
  const [adding, setAdding] = useState(false);
  const [pickValue, setPickValue] = useState('');

  if (current.length === 0 && !editable) {
    return <span className="text-text-muted">—</span>;
  }

  const currentIds = new Set(current.map((r) => Number(r.id)));
  const disponiveis = (usersQ.data ?? []).filter(
    (u) => !currentIds.has(Number(u.id)),
  );

  function saveWithReason(userIds: number[]) {
    const motivo = window.prompt('Informe o motivo da troca de técnico (obrigatório):', '');
    if (motivo == null) return;
    const trimmed = motivo.trim();
    if (!trimmed) {
      window.alert('O motivo da troca de técnico é obrigatório.');
      return;
    }
    onSave(userIds, trimmed);
  }

  function remove(userId: number) {
    saveWithReason(Array.from(currentIds).filter((id) => id !== userId));
  }
  function add(userId: number) {
    saveWithReason([...Array.from(currentIds), userId]);
    setAdding(false);
    setPickValue('');
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {current.map((r) => (
        <span
          key={r.id}
          className="inline-flex items-center gap-1 rounded-full bg-surface2 px-2 py-[2px] text-[0.78rem] text-text"
        >
          {r.name ?? r.email ?? `#${r.id}`}
          {editable && (
            <button
              type="button"
              onClick={() => remove(Number(r.id))}
              disabled={saving}
              aria-label={`Remover ${r.name ?? r.email}`}
              className="text-text-muted hover:text-danger leading-none"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {current.length === 0 && (
        <span className="text-text-muted text-[0.82rem]">Nenhum</span>
      )}
      {editable && !adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={saving}
          className="text-[0.78rem] text-accent hover:underline"
        >
          + adicionar
        </button>
      )}
      {editable && adding && (
        <select
          autoFocus
          value={pickValue}
          onChange={(e) => {
            const v = e.target.value;
            setPickValue(v);
            if (v) add(Number(v));
          }}
          onBlur={() => {
            if (!pickValue) {
              setAdding(false);
            }
          }}
          className="rounded-md bg-elevated border-[1.5px] border-accent px-2 py-1 text-[0.82rem] text-text outline-none"
        >
          <option value="">Selecione um usuário…</option>
          {disponiveis.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email ?? `#${u.id}`}
            </option>
          ))}
        </select>
      )}
      {saving && <span className="text-[0.7rem] text-text-muted">salvando…</span>}
    </div>
  );
}

function formatRaw(value: string | number | boolean | string[] | null, type: DetailFieldType): string {
  if (value == null) return '';
  if (Array.isArray(value)) return JSON.stringify(value);
  if (type === 'date' && typeof value === 'string') {
    // converte 'YYYY-MM-DD...' ou 'YYYY-MM-DDTHH...' pra YYYY-MM-DD
    return value.slice(0, 10);
  }
  return String(value);
}

function normalize(
  value: string | boolean,
  type: DetailFieldType,
): string | number | boolean | null {
  if (typeof value === 'boolean') return value;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (type === 'number') {
    const n = Number(trimmed.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
}
